import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { legacyRoutes } from '@/lib/legacy';
import { getAccountTicketCounts, getAccountQmsTicketCount } from '../api';
import type { AccountMetrics } from '../types';
import SupportTicketsSection from './SupportTicketsSection';
import QmsTicketsSection from './QmsTicketsSection';

interface TicketsTabProps {
  accountId: string;
  metrics: AccountMetrics;
}

export default function TicketsTab({
  accountId,
  metrics,
}: TicketsTabProps) {
  // Fetch support ticket counts
  const { data: supportCounts, isLoading: supportCountsLoading } = useQuery({
    queryKey: ['account-ticket-counts', accountId],
    queryFn: () => getAccountTicketCounts(accountId),
    enabled: !!accountId,
  });

  // Fetch QMS ticket count
  const { data: qmsCount, isLoading: qmsCountLoading } = useQuery({
    queryKey: ['account-qms-ticket-count', accountId],
    queryFn: () => getAccountQmsTicketCount(accountId),
    enabled: !!accountId,
  });

  const supportTicketCount = supportCounts?.open ?? metrics?.open_tickets ?? 0;
  const qmsTicketCount = qmsCount ?? 0;

  // Show loading indicator in tab counts
  const supportTabLabel = supportCountsLoading && !supportCounts
    ? 'Support (...)'
    : `Support (${supportTicketCount})`;
  const qmsTabLabel = qmsCountLoading && qmsCount === undefined
    ? 'QMS (...)'
    : `QMS (${qmsTicketCount})`;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Tickets</CardTitle>
            <CardDescription>
              {supportTicketCount > 0 || qmsTicketCount > 0 ? (
                <>
                  {supportTicketCount} support ticket{supportTicketCount !== 1 ? 's' : ''}
                  {metrics?.critical_tickets > 0 && (
                    <span className="text-destructive ml-1">
                      ({metrics.critical_tickets} critical)
                    </span>
                  )}
                  {qmsTicketCount > 0 && (
                    <span className="ml-2">
                      &middot; {qmsTicketCount} QMS ticket{qmsTicketCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </>
              ) : (
                'No tickets found for this account'
              )}
            </CardDescription>
          </div>
          <a href={legacyRoutes.createTicket(accountId)}>
            <Button size="sm">Create Support Ticket</Button>
          </a>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="support" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="support">
              {supportTabLabel}
            </TabsTrigger>
            <TabsTrigger value="qms">
              {qmsTabLabel}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="support">
            <SupportTicketsSection accountId={accountId} />
          </TabsContent>

          <TabsContent value="qms">
            <QmsTicketsSection accountId={accountId} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
